/**
 * shared/pmove/stance.ts — PM_CheckDuck, view height lerp, stance speed scale, prone checks.
 *
 * OWNER: PMOVE agent.
 *
 * Sources:
 *  - [PM §3 "View height / stance transitions" 0x941af0, PM_StanceSpeedScale 0x943ca0]:
 *    bounds 70/50/30, eye 11 prone / 40 crouch / standing dvar (ESTIMATE 60). Stand<->crouch eye
 *    moves at 180 u/s; prone transitions are time-based in percent; stance speed 1/0.65/0.15
 *    blended over 600 ms (going prone) and 400 ms (coming up from prone) / 200 ms.
 *  - The input logic (PM_CheckDuck), the waypoint tables and BG_CheckProne are [T5 bg_pmove.cpp /
 *    bg_misc.cpp]. T6 takes the prone transition times from the player anim lengths (0x616de0);
 *    we use the 600/400/200 ms of the stance speed blend for the eye too (ESTIMATE).
 *
 * Button mapping: Button.PRONE = T5 bit 8 (+prone), Button.CROUCH = T5 bit 9 (+stance crouch).
 * They are desired-stance LEVELS resolved by the input layer [CTL §6].
 */
import { createTraceResult } from '../collision/types';
import {
  ENTITYNUM_NONE, PLAYER_HEIGHT_CROUCH, PLAYER_HEIGHT_PRONE, PLAYER_MAXS_STAND, PM_PRONE_DOWN_MS,
  PM_PRONE_UP_FAST_MS, PM_PRONE_UP_MS, PM_STANCE_SCALE_CROUCH, PM_STANCE_SCALE_PRONE,
  VIEWHEIGHT_CROUCH, VIEWHEIGHT_LERP_SPEED, VIEWHEIGHT_PRONE,
} from '../constants';
import { angleVectors, type ReadonlyVec3, type Vec3, vnormalize } from '../math';
import { Button, PEv, PMF, PmType, Stance, type PlayerState } from '../types';
import { jumpActivateSlowdown } from './jump';
import {
  angleDelta, angleNorm180, effectiveStance, pitchForYawOnNormal, pmTrace, PM_PRONE_PITCH_RATE,
  PM_STANCE_CROUCH, PM_STANCE_PRONE, PRONE_FEET_DIST, PRONE_WAIST_DIST, STANCE_MASK_CLEAR,
  traceWalkable, vecToPitch, type Pm,
} from './pm';

const tr = createTraceResult();
const tmpMaxs: Vec3 = [0, 0, 0];

/** is_not_penetrating [T5 bg_pmove.cpp:529]: the hull fits at `pos` (others' bodies ignored). */
export function fitsAt(pm: Pm, pos: ReadonlyVec3, height: number): boolean {
  tmpMaxs[0] = pm.maxs[0]; tmpMaxs[1] = pm.maxs[1]; tmpMaxs[2] = height;
  pmTrace(pm, tr, pos, pm.mins, tmpMaxs, pos, pm.tracemask & STANCE_MASK_CLEAR);
  return !tr.allsolid && !tr.startsolid;
}

/** PM_CanStand [T5 bg_pmove.cpp:517]. */
export function canStand(pm: Pm): boolean {
  return (pm.ps.pm_flags & (PMF.PRONE | PMF.DUCKED)) === 0 || fitsAt(pm, pm.ps.origin, PLAYER_MAXS_STAND[2]);
}
/** PM_CanCrouch [T5 bg_pmove.cpp:2387] (only matters when prone). */
export function canCrouch(pm: Pm): boolean {
  return (pm.ps.pm_flags & PMF.PRONE) === 0 || fitsAt(pm, pm.ps.origin, PLAYER_HEIGHT_CROUCH);
}

/** Network stance from the effective stance. */
export function stanceOf(ps: PlayerState): Stance {
  const s = effectiveStance(ps);
  return s === PM_STANCE_PRONE ? Stance.PRONE : s === PM_STANCE_CROUCH ? Stance.CROUCH : Stance.STAND;
}

// =============================================================================================
// View height lerp
// =============================================================================================
interface Waypoint { readonly f: number; readonly h: number }
/** viewLerp_* waypoint tables (% of lerp time -> eye height) [T5 bg_pmove.cpp:81-132]. ESTIMATE for T6. */
const VIEWLERP_STAND_CROUCH: readonly Waypoint[] = [
  { f: 0, h: 60 }, { f: 1, h: 59.5 }, { f: 4, h: 58.5 }, { f: 30, h: 56 }, { f: 80, h: 44 }, { f: 90, h: 41.5 }, { f: 95, h: 40.5 }, { f: 100, h: 40 },
];
const VIEWLERP_CROUCH_STAND: readonly Waypoint[] = [
  { f: 0, h: 40 }, { f: 5, h: 40.5 }, { f: 10, h: 41.5 }, { f: 20, h: 44 }, { f: 70, h: 56 }, { f: 96, h: 58.5 }, { f: 99, h: 59.5 }, { f: 100, h: 60 },
];
const VIEWLERP_CROUCH_PRONE: readonly Waypoint[] = [
  { f: 0, h: 40 }, { f: 11, h: 38 }, { f: 22, h: 33 }, { f: 34, h: 25 }, { f: 45, h: 16 }, { f: 50, h: 15 }, { f: 55, h: 16 }, { f: 70, h: 18 }, { f: 90, h: 17 }, { f: 100, h: 11 },
];
const VIEWLERP_PRONE_CROUCH: readonly Waypoint[] = [
  { f: 0, h: 11 }, { f: 5, h: 10 }, { f: 30, h: 21 }, { f: 50, h: 25 }, { f: 67, h: 31 }, { f: 83, h: 34 }, { f: 100, h: 40 },
];

/** PM_ViewHeightTableLerp [T5 bg_pmove.cpp:4450]. The stand end of the stand tables follows the standing dvar. */
function tableLerp(iFrac: number, table: readonly Waypoint[], standH: number): number {
  const h = (w: Waypoint) => (w.h === 60 ? standH : w.h);
  if (!iFrac) return h(table[0]);
  for (let i = 1; i < table.length; i++) {
    const cur = table[i];
    if (iFrac === cur.f) return h(cur);
    if (cur.f > iFrac) {
      const prev = table[i - 1];
      const e = (iFrac - prev.f) / (cur.f - prev.f);
      return (h(cur) - h(prev)) * e + h(prev);
    }
  }
  return h(table[0]);
}

/**
 * Lerp duration for a timed view-height transition. T6 FUN_00616de0 (pmove_a.c:17498, fixed
 * constants, not anim lengths): target 11 -> 600 ms, target 40 -> 200 ms down / 400 ms up,
 * else (standing) 200 ms.
 */
export function viewHeightLerpTime(lerpTarget: number, lerpDown: number): number {
  if (lerpTarget === VIEWHEIGHT_PRONE) return PM_PRONE_DOWN_MS;
  if (lerpTarget !== VIEWHEIGHT_CROUCH) return PM_PRONE_UP_FAST_MS;
  return lerpDown ? PM_PRONE_UP_FAST_MS : PM_PRONE_UP_MS;
}

function lerpTable(ps: PlayerState): readonly Waypoint[] {
  if (ps.viewHeightLerpTarget === VIEWHEIGHT_PRONE) return VIEWLERP_CROUCH_PRONE;
  if (ps.viewHeightLerpTarget === VIEWHEIGHT_CROUCH) return ps.viewHeightLerpDown ? VIEWLERP_STAND_CROUCH : VIEWLERP_PRONE_CROUCH;
  return VIEWLERP_CROUCH_STAND;
}

/**
 * PM_ViewHeightAdjust (T6 0x941af0, extracted/t6mp-re/dec/pmove_a.c:6051-6240): the 180 u/s linear
 * move (@0xbe400c) runs only when the target is NOT 11, 40 or the standing height (pmove_a.c:6097);
 * targets 11/40/standing — including stand <-> crouch — are timed waypoint lerps whose durations come
 * from FUN_00616de0 (pmove_a.c:17498): 600 / 200 down / 400 up / 200. NOTE research/pmove-t6mp.md §3
 * ("Standing <-> crouch at 180 u/s") misreads this gate. Structure and the mid-lerp reversal match
 * [T5 bg_pmove.cpp:4252].
 */
export function viewHeightAdjust(pm: Pm): void {
  const ps = pm.ps;
  const standH = pm.ctx.dvars.standingViewHeight;
  if (!ps.viewHeightTarget || ps.viewHeightCurrent === 0) {
    ps.viewHeightCurrent = ps.pm_type === PmType.SPECTATOR ? 0 : ps.viewHeightTarget;
    return;
  }
  if (ps.viewHeightCurrent === ps.viewHeightTarget && !ps.viewHeightLerpTime) return;
  const target = ps.viewHeightTarget;
  if (target !== VIEWHEIGHT_PRONE && target !== VIEWHEIGHT_CROUCH && target !== standH) {
    moveEyeLinear(pm);
    return;
  }
  let iLerpFrac = 0;
  if (ps.viewHeightLerpTime) {
    const iLerpTime = viewHeightLerpTime(ps.viewHeightLerpTarget, ps.viewHeightLerpDown);
    iLerpFrac = Math.trunc((100 * (pm.cmd.serverTime - ps.viewHeightLerpTime)) / iLerpTime);
    if (iLerpFrac < 0) iLerpFrac = 0;
    else if (iLerpFrac > 100) iLerpFrac = 100;
    if (iLerpFrac === 100) {
      ps.viewHeightCurrent = ps.viewHeightLerpTarget === 60 ? standH : ps.viewHeightLerpTarget;
      ps.viewHeightLerpTime = 0;
    } else {
      ps.viewHeightCurrent = tableLerp(iLerpFrac, lerpTable(ps), standH);
    }
  }
  if (ps.viewHeightLerpTime) {
    // the wanted stance reversed mid-lerp: run the same lerp backwards from where it is
    const lt = ps.viewHeightLerpTarget;
    if (target !== lt && ((target > lt && ps.viewHeightLerpDown) || (target < lt && !ps.viewHeightLerpDown))) {
      iLerpFrac = 100 - iLerpFrac;
      ps.viewHeightLerpDown ^= 1;
      if (ps.viewHeightLerpDown) {
        if (lt === 60) ps.viewHeightLerpTarget = VIEWHEIGHT_CROUCH;
        else if (lt === VIEWHEIGHT_CROUCH) ps.viewHeightLerpTarget = VIEWHEIGHT_PRONE;
      } else if (lt === VIEWHEIGHT_PRONE) ps.viewHeightLerpTarget = VIEWHEIGHT_CROUCH;
      else if (lt === VIEWHEIGHT_CROUCH) ps.viewHeightLerpTarget = 60;
      if (iLerpFrac === 100) {
        ps.viewHeightCurrent = ps.viewHeightLerpTarget === 60 ? standH : ps.viewHeightLerpTarget;
        ps.viewHeightLerpTime = 0;
      } else {
        const iLerpTime = viewHeightLerpTime(ps.viewHeightLerpTarget, ps.viewHeightLerpDown);
        ps.viewHeightLerpTime = pm.cmd.serverTime - Math.trunc(iLerpFrac * 0.0099999998 * iLerpTime);
      }
    }
    return;
  }
  if (ps.viewHeightCurrent === target) return;
  // Start a timed lerp [T6 0x941af0, pmove_a.c:6158-6198]. Every target that reaches here
  // (11, 40, standing) is timed; stand <-> crouch is a 200 ms table lerp, not 180 u/s.
  ps.viewHeightLerpTime = pm.cmd.serverTime;
  if (target === VIEWHEIGHT_PRONE) {
    // pmove_a.c:6165-6173: down; via crouch height first when above 40
    ps.viewHeightLerpDown = 1;
    ps.viewHeightLerpTarget = ps.viewHeightCurrent > VIEWHEIGHT_CROUCH ? VIEWHEIGHT_CROUCH : VIEWHEIGHT_PRONE;
  } else if (target === VIEWHEIGHT_CROUCH) {
    // pmove_a.c:6174-6181: from above -> down to 40 (200 ms); from prone -> up to 40 (400 ms)
    ps.viewHeightLerpDown = ps.viewHeightCurrent > VIEWHEIGHT_CROUCH ? 1 : 0;
    ps.viewHeightLerpTarget = VIEWHEIGHT_CROUCH;
  } else {
    // standing, pmove_a.c:6182-6196: up; straight to standing from >= 40, else via 40 first.
    // Standing is stored as the 60 marker (resolved to the dvar on completion), as elsewhere here.
    ps.viewHeightLerpDown = 0;
    ps.viewHeightLerpTarget = ps.viewHeightCurrent >= VIEWHEIGHT_CROUCH ? 60 : VIEWHEIGHT_CROUCH;
  }
}

function moveEyeLinear(pm: Pm): void {
  const ps = pm.ps;
  ps.viewHeightLerpTime = 0;
  const step = VIEWHEIGHT_LERP_SPEED * pm.frametime;
  if (ps.viewHeightTarget <= ps.viewHeightCurrent) {
    ps.viewHeightCurrent -= step;
    if (ps.viewHeightTarget >= ps.viewHeightCurrent) ps.viewHeightCurrent = ps.viewHeightTarget;
  } else {
    ps.viewHeightCurrent += step;
    if (ps.viewHeightCurrent >= ps.viewHeightTarget) ps.viewHeightCurrent = ps.viewHeightTarget;
  }
}

/** Fraction of the current timed lerp towards `toHeight` (0 when not in that lerp) [T5 PM_GetViewHeightLerp]. */
function viewHeightLerpFrac(pm: Pm, fromHeight: number, toHeight: number): number {
  const ps = pm.ps;
  if (!ps.viewHeightLerpTime) return 0;
  if (toHeight !== ps.viewHeightLerpTarget) return 0;
  if (toHeight === VIEWHEIGHT_CROUCH && (fromHeight !== VIEWHEIGHT_PRONE || ps.viewHeightLerpDown)) return 0;
  const f = (pm.cmd.serverTime - ps.viewHeightLerpTime) / viewHeightLerpTime(ps.viewHeightLerpTarget, ps.viewHeightLerpDown);
  return f < 0 ? 0 : f > 1 ? 1 : f;
}

/**
 * PM_StanceSpeedScale (0x943ca0) [PM §3]: 1 / 0.65 / 0.15, blended crouch->prone over 600 ms and
 * prone->crouch over 400 ms. UNCERTAIN in T6 [PM §3]: which lerps blend; we follow T5
 * PM_CmdScaleForStance (only lerps that start or end at prone blend).
 */
export function cmdScaleForStance(pm: Pm): number {
  if (pm.ps.pm_flags & PMF.DIVE) return 1;
  let f = viewHeightLerpFrac(pm, VIEWHEIGHT_CROUCH, VIEWHEIGHT_PRONE);
  if (f !== 0) return PM_STANCE_SCALE_PRONE * f + (1 - f) * PM_STANCE_SCALE_CROUCH;
  f = viewHeightLerpFrac(pm, VIEWHEIGHT_PRONE, VIEWHEIGHT_CROUCH);
  if (f !== 0) return PM_STANCE_SCALE_CROUCH * f + (1 - f) * PM_STANCE_SCALE_PRONE;
  const s = effectiveStance(pm.ps);
  if (s === PM_STANCE_PRONE) return PM_STANCE_SCALE_PRONE;
  if (s === PM_STANCE_CROUCH) return PM_STANCE_SCALE_CROUCH;
  return 1;
}

// =============================================================================================
// BG_CheckProne [T5 bg_misc.cpp:2990]
// =============================================================================================
const cpMins: Vec3 = [-6, -6, -6];
const cpMaxs: Vec3 = [6, 6, 6];
const cpZero: Vec3 = [0, 0, 0];
const cpAng: Vec3 = [0, 0, 0];
const cpDir: Vec3 = [0, 0, 0];
const cpStart: Vec3 = [0, 0, 0];
const cpEnd: Vec3 = [0, 0, 0];
const cpFeet: Vec3 = [0, 0, 0];
const cpWaist: Vec3 = [0, 0, 0];
const cpTmp: Vec3 = [0, 0, 0];
const cpBoxMins: Vec3 = [0, 0, 0];
const cpBoxMaxs: Vec3 = [0, 0, 0];

function lerp3(out: Vec3, a: ReadonlyVec3, b: ReadonlyVec3, f: number): void {
  out[0] = a[0] + (b[0] - a[0]) * f; out[1] = a[1] + (b[1] - a[1]) * f; out[2] = a[2] + (b[2] - a[2]) * f;
}

/**
 * Can a prone body (feet `feetDist` behind `pos` along `yaw`) lie here? Port of T5 BG_CheckProne
 * (torso/waist pitch outputs dropped; `enable_new_prone_check` assumed on).
 */
export function checkProne(
  pm: Pm, pos: ReadonlyVec3, fSize: number, fHeight: number, fYaw: number,
  isAlreadyProne: boolean, isOnGround: boolean, groundIsWalkable: boolean, feetDist: number,
): boolean {
  const mask = pm.tracemask & STANCE_MASK_CLEAR;
  let bFirstTraceHit = false;
  let success = true;
  const fWaistDistance = PRONE_WAIST_DIST;
  if (!isAlreadyProne) {
    cpBoxMins[0] = -fSize; cpBoxMins[1] = -fSize; cpBoxMins[2] = 0;
    cpBoxMaxs[0] = fSize; cpBoxMaxs[1] = fSize; cpBoxMaxs[2] = fHeight;
    cpEnd[0] = pos[0]; cpEnd[1] = pos[1]; cpEnd[2] = pos[2] + 10;
    pmTrace(pm, tr, pos, cpBoxMins, cpBoxMaxs, cpEnd, mask);
    if (tr.allsolid) return false;
  }
  if (isOnGround && !groundIsWalkable) return false;
  cpAng[0] = 0; cpAng[1] = fYaw - 180; cpAng[2] = 0;
  angleVectors(cpAng, cpDir, null, null);
  const fTraceHeight = fHeight - 6;
  cpStart[0] = pos[0]; cpStart[1] = pos[1]; cpStart[2] = pos[2] + fTraceHeight;
  const vFeet = feetDist - 6;
  cpEnd[0] = cpStart[0] + vFeet * cpDir[0]; cpEnd[1] = cpStart[1] + vFeet * cpDir[1]; cpEnd[2] = cpStart[2] + vFeet * cpDir[2];
  pmTrace(pm, tr, cpStart, cpMins, cpMaxs, cpEnd, mask);
  let fWaistTraceDist: number;
  if (tr.fraction >= 1) {
    fWaistTraceDist = feetDist;
  } else {
    if (!isOnGround) return false;
    bFirstTraceHit = true;
    fWaistTraceDist = vFeet * tr.fraction + 6;
    if (fSize + 2 > fWaistTraceDist) return false;
    if (fTraceHeight * 0.69999999 + fWaistDistance > fWaistTraceDist) {
      bFirstTraceHit = false;
      cpEnd[2] += 22; // try with the feet raised (lying on a slope / step)
      cpTmp[0] = cpEnd[0] - cpStart[0]; cpTmp[1] = cpEnd[1] - cpStart[1]; cpTmp[2] = cpEnd[2] - cpStart[2];
      const fPitchDiff = vnormalize(cpDir, cpTmp);
      pmTrace(pm, tr, cpStart, cpMins, cpMaxs, cpEnd, mask);
      if (tr.fraction >= 1) {
        fWaistTraceDist = feetDist;
      } else {
        bFirstTraceHit = true;
        fWaistTraceDist = tr.fraction * fPitchDiff + 6;
        if (fTraceHeight * 0.69999999 + fWaistDistance > fWaistTraceDist) return false;
      }
    }
  }
  lerp3(cpFeet, cpStart, cpEnd, tr.fraction);
  // waist: straight down from behind the torso
  cpStart[0] = pos[0] + fWaistDistance * cpDir[0];
  cpStart[1] = pos[1] + fWaistDistance * cpDir[1];
  cpStart[2] = pos[2] + fWaistDistance * cpDir[2] + fTraceHeight;
  const waistDrop = fSize * 2.5 + fTraceHeight - 6;
  cpEnd[0] = cpStart[0]; cpEnd[1] = cpStart[1]; cpEnd[2] = cpStart[2] - waistDrop;
  pmTrace(pm, tr, cpStart, cpMins, cpMaxs, cpEnd, mask);
  if (tr.fraction !== 1) {
    if (!traceWalkable(pm, tr)) return false;
    const v26 = waistDrop * tr.fraction + 6;
    lerp3(cpWaist, cpStart, cpEnd, tr.fraction);
    cpWaist[2] -= 6;
    let failed = false;
    if (bFirstTraceHit) {
      if (-0.75 * v26 > fWaistTraceDist - v26) failed = true;
      else {
        cpTmp[0] = 6 * cpDir[0] + (cpFeet[0] - cpWaist[0]);
        cpTmp[1] = 6 * cpDir[1] + (cpFeet[1] - cpWaist[1]);
        cpTmp[2] = 6 * cpDir[2] + (cpFeet[2] - cpWaist[2]) + 6;
        vnormalize(cpTmp);
        const v24 = vFeet - fWaistDistance;
        cpEnd[0] = (pos[0] + vFeet * cpDir[0] + (cpStart[0] + v24 * cpTmp[0])) * 0.5;
        cpEnd[1] = (pos[1] + vFeet * cpDir[1] + (cpStart[1] + v24 * cpTmp[1])) * 0.5;
        cpEnd[2] = cpStart[2] + v24 * cpTmp[2];
        pmTrace(pm, tr, cpStart, cpMins, cpMaxs, cpEnd, mask);
        if (tr.fraction < 1) {
          lerp3(cpStart, cpStart, cpEnd, tr.fraction);
          cpStart[2] += 18; cpEnd[2] += 18;
          pmTrace(pm, tr, cpStart, cpMins, cpMaxs, cpEnd, mask);
          if (tr.fraction < 1) failed = true;
        }
        if (!failed) lerp3(cpFeet, cpStart, cpEnd, tr.fraction);
      }
    }
    if (!failed) {
      // feet must rest on walkable ground
      cpStart[0] = cpFeet[0]; cpStart[1] = cpFeet[1]; cpStart[2] = cpFeet[2];
      cpEnd[0] = cpFeet[0]; cpEnd[1] = cpFeet[1]; cpEnd[2] = cpFeet[2] - ((cpFeet[2] - cpWaist[2]) * 2 + fSize);
      pmTrace(pm, tr, cpStart, cpMins, cpMaxs, cpEnd, mask);
      if (tr.fraction !== 1) {
        if (!traceWalkable(pm, tr)) return false;
        lerp3(cpFeet, cpStart, cpEnd, tr.fraction);
        cpFeet[2] -= 6;
        cpTmp[0] = pos[0] - cpWaist[0]; cpTmp[1] = pos[1] - cpWaist[1]; cpTmp[2] = pos[2] - cpWaist[2];
        const torsoPitch = angleNorm180(vecToPitch(cpTmp));
        cpTmp[0] = cpWaist[0] - cpFeet[0]; cpTmp[1] = cpWaist[1] - cpFeet[1]; cpTmp[2] = cpWaist[2] - cpFeet[2];
        const waistPitch = angleNorm180(vecToPitch(cpTmp));
        const pitchDiff = angleNorm180(torsoPitch - waistPitch);
        if (pitchDiff < -50 || pitchDiff > 70) success = false;
        cpStart[0] = pos[0]; cpStart[1] = pos[1]; cpStart[2] = pos[2] + 5;
        cpEnd[0] = cpWaist[0]; cpEnd[1] = cpWaist[1]; cpEnd[2] = cpWaist[2] + 5;
        pmTrace(pm, tr, cpStart, cpZero, cpZero, cpEnd, mask);
        if (tr.fraction < 1) success = false;
        if (success) return true;
      }
    }
  }
  // fail:
  return !isOnGround;
}

/** PlayerProneAllowed [T5 bg_pmove.cpp:604]. */
function playerProneAllowed(pm: Pm): boolean {
  const ps = pm.ps;
  const blocks = pm.ctx.weaponDef(ps.weapon).raw.blocksProne;
  if (typeof blocks === 'number' && blocks) return false;
  if (ps.pm_flags & PMF.PRONE) return true;
  if (ps.groundEntityNum === ENTITYNUM_NONE) return false;
  if (ps.waterlevel >= 1) return false;
  return checkProne(pm, ps.origin, pm.maxs[0], PLAYER_HEIGHT_PRONE, ps.viewangles[1], false, true, true, PRONE_FEET_DIST);
}

/**
 * PM_VerifyPronePosition [T5 bg_slidemove.cpp:2691]: a prone move that ends where the body
 * does not fit is undone. Returns false when the fallback position was used.
 */
export function verifyPronePosition(pm: Pm, fallbackOrg: ReadonlyVec3, fallbackVel: ReadonlyVec3): boolean {
  const ps = pm.ps;
  if (!(ps.pm_flags & PMF.PRONE)) return true;
  if (checkProne(pm, ps.origin, 15, PLAYER_HEIGHT_PRONE, ps.proneDirection, true, true, true, PRONE_FEET_DIST)) return true;
  const so0 = ps.origin[0], so1 = ps.origin[1], so2 = ps.origin[2];
  const sv0 = ps.velocity[0], sv1 = ps.velocity[1], sv2 = ps.velocity[2];
  ps.origin[0] = fallbackOrg[0]; ps.origin[1] = fallbackOrg[1]; ps.origin[2] = fallbackOrg[2];
  ps.velocity[0] = fallbackVel[0]; ps.velocity[1] = fallbackVel[1]; ps.velocity[2] = fallbackVel[2];
  if (checkProne(pm, ps.origin, 15, PLAYER_HEIGHT_PRONE, ps.proneDirection, true, true, true, PRONE_FEET_DIST)) return false;
  ps.origin[0] = so0; ps.origin[1] = so1; ps.origin[2] = so2;
  ps.velocity[0] = sv0; ps.velocity[1] = sv1; ps.velocity[2] = sv2;
  return true;
}

// =============================================================================================
// PM_CheckDuck [T5 bg_pmove.cpp:3808]
// =============================================================================================
const settleEnd: Vec3 = [0, 0, 0];
const settleMask = (pm: Pm) => pm.tracemask & STANCE_MASK_CLEAR;

function setHull(pm: Pm, height: number): void {
  pm.mins[0] = -15; pm.mins[1] = -15; pm.mins[2] = 0;
  pm.maxs[0] = 15; pm.maxs[1] = 15; pm.maxs[2] = height;
}

/** set_stance [T5 bg_pmove.cpp:5628]: hull from the current flags. */
export function setStanceHull(pm: Pm): void {
  const f = pm.ps.pm_flags;
  setHull(pm, f & PMF.PRONE ? PLAYER_HEIGHT_PRONE : f & PMF.DUCKED ? PLAYER_HEIGHT_CROUCH : PLAYER_MAXS_STAND[2]);
}

export function checkDuck(pm: Pm): void {
  const ps = pm.ps;
  const standH = pm.ctx.dvars.standingViewHeight;
  if (ps.pm_type === PmType.SPECTATOR) {
    pm.mins[0] = -8; pm.mins[1] = -8; pm.mins[2] = -8;
    pm.maxs[0] = 8; pm.maxs[1] = 8; pm.maxs[2] = 16;
    ps.pm_flags &= ~(PMF.PRONE | PMF.DUCKED);
    ps.viewHeightTarget = 0;
    ps.viewHeightCurrent = 0;
    return;
  }
  const stanceBefore = stanceOf(ps);
  const bWasProne = (ps.pm_flags & PMF.PRONE) !== 0;
  setHull(pm, PLAYER_MAXS_STAND[2]);
  const buttons = pm.cmd.buttons;
  if (bWasProne && ps.waterlevel >= 1) {
    ps.viewHeightTarget = VIEWHEIGHT_CROUCH;
    ps.pm_flags &= ~PMF.PRONE;
    if (ps.waterlevel >= 3) {
      ps.viewHeightTarget = standH;
      ps.pm_flags &= ~(PMF.PRONE | PMF.DUCKED);
    }
    viewHeightAdjust(pm);
    emitStanceChange(pm, stanceBefore);
    return;
  }
  if ((ps.pm_flags & (PMF.PRONE | PMF.DUCKED)) && ps.waterlevel >= 2) {
    ps.viewHeightTarget = standH;
    ps.pm_flags &= ~(PMF.PRONE | PMF.DUCKED);
    viewHeightAdjust(pm);
    emitStanceChange(pm, stanceBefore);
    return;
  }
  if (ps.pm_flags & PMF.SPRINTING) {
    // sprinting always stands you up
    ps.viewHeightTarget = standH;
    ps.pm_flags &= ~(PMF.PRONE | PMF.DUCKED);
    viewHeightAdjust(pm);
    emitStanceChange(pm, stanceBefore);
    return;
  }
  // PM_IsPlayerFrozenByWeapon (freezeMovementWhenFiring) is never set on the SnS weapons
  const frozen = (ps.pm_flags & (PMF.FROZEN | PMF.UNK_0x800)) !== 0;
  if (!frozen) {
    if (ps.pm_type === PmType.LASTSTAND || ps.pm_type === PmType.DEAD) {
      ps.pm_flags &= ~PMF.PRONE;
      ps.pm_flags |= PMF.DUCKED;
    } else if (!(ps.pm_flags & PMF.DIVE)) {
      let b = buttons;
      if ((ps.pm_flags & PMF.LADDER) && (b & (Button.PRONE | Button.CROUCH))) {
        b &= ~(Button.PRONE | Button.CROUCH); // no stance changes on a ladder
        pm.cmd.buttons = b;
      }
      if (!(b & Button.PRONE) || (ps.pm_flags & PMF.FROZEN)) {
        if (b & Button.CROUCH) {
          if (ps.waterlevel >= 2) {
            // blocked (T5 event 8)
          } else if (ps.pm_flags & PMF.PRONE) {
            if (fitsAt(pm, ps.origin, PLAYER_HEIGHT_CROUCH)) {
              ps.pm_flags &= ~PMF.PRONE;
              ps.pm_flags |= PMF.DUCKED;
            }
          } else {
            ps.pm_flags |= PMF.DUCKED;
          }
        } else if (ps.pm_flags & PMF.PRONE) {
          if (fitsAt(pm, ps.origin, PLAYER_MAXS_STAND[2])) {
            ps.pm_flags &= ~(PMF.PRONE | PMF.DUCKED);
          } else if (fitsAt(pm, ps.origin, PLAYER_HEIGHT_CROUCH)) {
            ps.pm_flags &= ~PMF.PRONE;
            ps.pm_flags |= PMF.DUCKED;
          }
        } else if (ps.pm_flags & PMF.DUCKED) {
          if (fitsAt(pm, ps.origin, PLAYER_MAXS_STAND[2])) ps.pm_flags &= ~PMF.DUCKED;
        }
      } else if (playerProneAllowed(pm)) {
        ps.pm_flags |= PMF.PRONE;
        ps.pm_flags &= ~PMF.DUCKED;
      }
    } else {
      // diving: force prone
      ps.pm_flags &= ~PMF.DUCKED;
      ps.viewHeightTarget = VIEWHEIGHT_PRONE;
    }
  }
  if (!ps.viewHeightLerpTime) {
    if (ps.pm_flags & PMF.PRONE) {
      if (ps.viewHeightTarget === standH) {
        ps.viewHeightTarget = VIEWHEIGHT_CROUCH; // stand -> prone goes through crouch
      } else if (ps.viewHeightTarget !== VIEWHEIGHT_PRONE) {
        ps.viewHeightTarget = VIEWHEIGHT_PRONE;
        jumpActivateSlowdown(ps); // going prone counts as a landing [T5 PM_CheckDuck]
      }
    } else if (ps.viewHeightTarget === VIEWHEIGHT_PRONE) {
      ps.viewHeightTarget = VIEWHEIGHT_CROUCH;
    } else if (ps.pm_flags & PMF.DUCKED) {
      ps.viewHeightTarget = VIEWHEIGHT_CROUCH;
    } else {
      ps.viewHeightTarget = standH;
    }
  }
  viewHeightAdjust(pm);
  const iStance = effectiveStance(ps);
  if (iStance === PM_STANCE_PRONE) {
    pm.maxs[2] = PLAYER_HEIGHT_PRONE;
    ps.pm_flags |= PMF.PRONE;
    ps.pm_flags &= ~PMF.DUCKED;
  } else if (iStance === PM_STANCE_CROUCH) {
    pm.maxs[2] = PLAYER_HEIGHT_CROUCH;
    ps.pm_flags |= PMF.DUCKED;
    ps.pm_flags &= ~PMF.PRONE;
  } else {
    pm.maxs[2] = PLAYER_MAXS_STAND[2];
    ps.pm_flags &= ~(PMF.PRONE | PMF.DUCKED);
  }
  if (ps.pm_flags & PMF.DIVE) {
    pm.maxs[2] = ps.viewHeightCurrent >= 30 ? (ps.viewHeightCurrent >= 50 ? PLAYER_MAXS_STAND[2] : PLAYER_HEIGHT_CROUCH) : PLAYER_HEIGHT_PRONE;
  }
  if ((ps.pm_flags & PMF.PRONE) && !bWasProne) settleProne(pm);
  emitStanceChange(pm, stanceBefore);
}

function emitStanceChange(pm: Pm, before: Stance): void {
  const after = stanceOf(pm.ps);
  if (after !== before) pm.ctx.addEvent(pm.ps, PEv.STANCE, after);
}

/** Just went prone: settle the shorter hull onto the ground and set the body direction [T5 PM_CheckDuck tail]. */
function settleProne(pm: Pm): void {
  const ps = pm.ps;
  const mask = settleMask(pm);
  settleEnd[0] = ps.origin[0]; settleEnd[1] = ps.origin[1]; settleEnd[2] = ps.origin[2] + 10;
  pmTrace(pm, tr, ps.origin, pm.mins, pm.maxs, settleEnd, mask);
  lerp3(settleEnd, ps.origin, settleEnd, tr.fraction);
  pmTrace(pm, tr, settleEnd, pm.mins, pm.maxs, ps.origin, mask);
  lerp3(ps.origin, settleEnd, ps.origin, tr.fraction);
  ps.proneDirection = ps.viewangles[1];
  settleEnd[0] = ps.origin[0]; settleEnd[1] = ps.origin[1]; settleEnd[2] = ps.origin[2] - 0.25;
  pmTrace(pm, tr, ps.origin, pm.mins, pm.maxs, settleEnd, mask);
  if (tr.startsolid || tr.fraction >= 1 || ps.pm_type === PmType.LASTSTAND) ps.proneDirectionPitch = 0;
  else ps.proneDirectionPitch = pitchForYawOnNormal(ps.proneDirection, tr.normal);
  const delta = angleDelta(ps.proneDirectionPitch, ps.viewangles[0]);
  if (delta < -45) ps.proneTorsoPitch = ps.viewangles[0] - 45;
  else if (delta > 45) ps.proneTorsoPitch = ps.viewangles[0] + 45;
  else ps.proneTorsoPitch = ps.proneDirectionPitch;
}

/** PM_UpdatePronePitch [T5 bg_pmove.cpp:1352]: body/torso pitch follow the ground at 70 deg/s. */
export function updatePronePitch(pm: Pm): void {
  const ps = pm.ps;
  if (!(ps.pm_flags & PMF.PRONE)) return;
  const n = pm.groundTrace.normal;
  const targ = pm.groundPlane ? pitchForYawOnNormal(ps.proneDirection, n) : 0;
  const rate = PM_PRONE_PITCH_RATE * pm.frametime;
  let d = angleDelta(targ, ps.proneDirectionPitch);
  if (d !== 0) {
    ps.proneDirectionPitch = angleNorm180(Math.abs(d) <= rate ? ps.proneDirectionPitch + d : ps.proneDirectionPitch + rate * (d < 0 ? -1 : 1));
  }
  const targTorso = pm.groundPlane ? pitchForYawOnNormal(ps.viewangles[1], n) : 0;
  d = angleDelta(targTorso, ps.proneTorsoPitch);
  if (d !== 0) {
    ps.proneTorsoPitch = angleNorm180(Math.abs(d) <= rate ? ps.proneTorsoPitch + d : ps.proneTorsoPitch + rate * (d < 0 ? -1 : 1));
  }
}
